// src/components/roads/RoadSystemWatchdog.js
import React, { useMemo, useState } from 'react';
import {
    Box, Flex, Text, HStack, VStack, Icon, Badge, Button, Progress, useColorModeValue, useToast, Divider, SimpleGrid
} from '@chakra-ui/react';
import { MdDataExploration, MdCloudSync, MdOutlineAutoAwesome, MdEdit } from 'react-icons/md';
import { motion } from 'framer-motion';
import { getAuthToken } from '../../utils/auth';
import { config } from '../../config';

const MotionBox = motion(Box);
const API_BASE = config.apiUrl;

// ─────────────────────────────────────────────────────────────────────────────
// EXPORTED LOGIC: Analyzes a single road for ML Readiness and SLA Breaches
// ─────────────────────────────────────────────────────────────────────────────
export const analyzeRoadCompliance = (road) => {
    const missingMLData = [];
    const meta = road.metadata || {};

    // 1. Check for ML-Critical Data
    if (!meta.surface_type) missingMLData.push('Surface Type');
    if (!meta.classification) missingMLData.push('Classification');
    if (!meta.length_m) missingMLData.push('Length (m)');
    if (road.latitude == null || road.longitude == null) missingMLData.push('Geo-Coordinates');
    if (meta.condition_score == null) missingMLData.push('Baseline Condition Score');

    // 2. Check for Overdue Inspections
    let isInspectionOverdue = false;
    let daysOverdue = 0;
    if (meta.next_inspection_due) {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Normalize to start of day
        const dueDate = new Date(meta.next_inspection_due);
        
        if (dueDate < today) {
            isInspectionOverdue = true;
            daysOverdue = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
        }
    }

    return {
        isReadyForML: missingMLData.length === 0,
        missingMLData,
        isInspectionOverdue,
        daysOverdue
    };
};

// ─────────────────────────────────────────────────────────────────────────────
// WATCHDOG PANEL COMPONENT: Displays network health and syncs with ML
// ─────────────────────────────────────────────────────────────────────────────
// 🚀 FIXED: Added onViewAllStarved to the component props!
export const RoadSystemWatchdogPanel = ({ roads, onSyncWithML, onViewAllStarved }) => {
    const [isSyncing, setIsSyncing] = useState(false);
    const toast = useToast();
    
    // 🚀 FIXED: ALL hooks are now declared at the top level, before any early returns!
    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('red.200', 'red.900');
    const aiBoxBg = useColorModeValue('pink.50', 'pink.900');
    const dividerColor = useColorModeValue('gray.200', 'gray.700');
    const aiBorderColor = useColorModeValue('pink.200', 'pink.800');
    const aiTextColor = useColorModeValue('gray.700', 'gray.300');
    const missingFieldBorderColor = useColorModeValue('gray.200', 'gray.600');

    // Calculate network-wide compliance
    const analytics = useMemo(() => {
        let mlUnreadyCount = 0;
        let overdueCount = 0;
        const missingDataTypes = {};

        (roads || []).forEach(road => {
            const { isReadyForML, missingMLData, isInspectionOverdue } = analyzeRoadCompliance(road);
            
            if (!isReadyForML) {
                mlUnreadyCount++;
                missingMLData.forEach(type => {
                    missingDataTypes[type] = (missingDataTypes[type] || 0) + 1;
                });
            }
            if (isInspectionOverdue) overdueCount++;
        });

        const mlReadinessPct = roads?.length > 0 ? Math.round(((roads.length - mlUnreadyCount) / roads.length) * 100) : 100;

        return { mlUnreadyCount, overdueCount, mlReadinessPct, missingDataTypes };
    }, [roads]);

    const handleNotifyML = async () => {
        setIsSyncing(true);
        try {
            const response = await fetch(`${API_BASE}/roads/watchdog/sync`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getAuthToken()}`
                }
            });
            
            if (!response.ok) throw new Error('Failed to sync with backend watchdog');
            
            const result = await response.json();
            
            if (onSyncWithML) onSyncWithML(analytics);
            
            toast({
                title: 'AI Engine Synchronized',
                description: `Successfully suspended RUL predictions for ${result.data?.ml_starved_count || analytics.mlUnreadyCount} data-starved assets.`,
                status: 'success',
                duration: 6000,
                isClosable: true
            });
        } catch (error) {
            toast({
                title: 'Sync Failed',
                description: error.message || 'Could not push the report to the AI Engine.',
                status: 'error',
                duration: 4000,
                isClosable: true
            });
        } finally {
            setIsSyncing(false);
        }
    };

    // The early return is perfectly safe now that all hooks are above it!
    if (analytics.mlUnreadyCount === 0 && analytics.overdueCount === 0) return null; 

    // Sort missing fields to show the most critical gaps first
    const topMissingFields = Object.entries(analytics.missingDataTypes)
        .sort((a, b) => b[1] - a[1]);

    return (
        <MotionBox initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <Box bg={cardBg} borderRadius="2xl" borderWidth="1px" borderColor={borderColor} p={4} boxShadow="sm" position="relative" overflow="hidden">
                <Box position="absolute" top={0} left={0} bottom={0} w="4px" bg="red.400" />
                
                <Flex justify="space-between" align="flex-start" wrap="wrap" gap={4}>
                    <HStack spacing={4} align="flex-start">
                        <Box bg="red.50" p={2.5} borderRadius="xl" mt={1}>
                            <Icon as={MdDataExploration} color="red.500" boxSize={6} />
                        </Box>
                        <Box>
                            <Text fontSize="md" fontWeight="800" color="red.700">Data Quality & SLA Watchdog</Text>
                            <HStack spacing={3} mt={1}>
                                {analytics.mlUnreadyCount > 0 && (
                                    <Badge colorScheme="orange" variant="subtle" borderRadius="md" px={2}>
                                        {analytics.mlUnreadyCount} Roads Missing ML Data
                                    </Badge>
                                )}
                                {analytics.overdueCount > 0 && (
                                    <Badge colorScheme="red" variant="subtle" borderRadius="md" px={2}>
                                        {analytics.overdueCount} Overdue Inspections
                                    </Badge>
                                )}
                            </HStack>
                        </Box>
                    </HStack>

                    <VStack align="flex-end" spacing={2} minW="250px">
                        <HStack justify="space-between" w="full">
                            <Text fontSize="xs" fontWeight="700" color="gray.500">Network ML Readiness</Text>
                            <Text fontSize="xs" fontWeight="900" color={analytics.mlReadinessPct < 80 ? 'orange.500' : 'green.500'}>
                                {analytics.mlReadinessPct}%
                            </Text>
                        </HStack>
                        <Progress value={analytics.mlReadinessPct} w="full" size="sm" borderRadius="full" colorScheme={analytics.mlReadinessPct < 80 ? 'orange' : 'green'} hasStripe isAnimated={analytics.mlReadinessPct < 100} />
                        
                        <Button 
                            size="xs" 
                            colorScheme="red" 
                            variant="outline" 
                            leftIcon={<Icon as={MdCloudSync} />}
                            onClick={handleNotifyML}
                            isLoading={isSyncing}
                            borderRadius="xl"
                            mt={1}
                        >
                            Push Report to AI Engine
                        </Button>
                    </VStack>
                </Flex>

                {/* AI Prescription Block */}
                {analytics.mlReadinessPct < 100 && (
                    <>
                        <Divider my={4} borderColor={dividerColor} />
                        <Box bg={aiBoxBg} p={4} borderRadius="xl" borderWidth="1px" borderColor={aiBorderColor}>
                            <HStack mb={2} justify="space-between" wrap="wrap">
                                <HStack>
                                    <Icon as={MdOutlineAutoAwesome} color="pink.500" boxSize={5} />
                                    <Text fontSize="sm" fontWeight="800" color="pink.700">AI Action Plan: Resolving Data Starvation</Text>
                                </HStack>
                                {/* 🚀 NEW: Button to trigger the table filter */}
                                <Button size="xs" colorScheme="pink" onClick={onViewAllStarved}>
                                    Filter Table: View All Starved Assets
                                </Button>
                            </HStack>
                            <Text fontSize="xs" color={aiTextColor} mb={3}>
                                FlowSense Machine Learning models require specific physical constraints to calculate accurate Remaining Useful Life (RUL). 
                                To restore predictive analytics to 100%, click the <Icon as={MdEdit} color="blue.500" display="inline" /> <b>Edit Road Data</b> action on the flagged rows below and populate the following missing metrics:
                            </Text>
                            
                            <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={2}>
                                {topMissingFields.map(([field, count]) => (
                                    <Box key={field} bg={cardBg} p={2} borderRadius="lg" borderWidth="1px" borderColor={missingFieldBorderColor}>
                                        <Text fontSize="2xs" fontWeight="700" color="gray.500" textTransform="uppercase">{field}</Text>
                                        <Text fontSize="sm" fontWeight="800" color="orange.500">{count} Missing</Text>
                                    </Box>
                                ))}
                            </SimpleGrid>
                        </Box>
                    </>
                )}
            </Box>
        </MotionBox>
    );
};